import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { RefreshCw } from 'lucide-react';
import { shiftMarketplaceApi } from '../services/api';
import { ShiftMarketplaceRequest } from '../types';
import { usePermissions } from '../context/PermissionsContext';

const parseLocalDateTime = (dateTimeStr: string): Date => {
  if (!dateTimeStr) return new Date();
  try {
    if (!dateTimeStr.includes('Z') && !dateTimeStr.includes('+') && !dateTimeStr.includes('-', 10)) {
      const [datePart, timePart] = dateTimeStr.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
      return new Date(year, month - 1, day, hour, minute, second || 0);
    }
    return parseISO(dateTimeStr);
  } catch {
    return new Date(dateTimeStr);
  }
};

const formatShiftRange = (start?: string, end?: string) => {
  if (!start || !end) return '—';
  const s = parseLocalDateTime(start);
  const e = parseLocalDateTime(end);
  return `${format(s, 'EEE d MMM HH:mm')} – ${format(e, 'HH:mm')}`;
};

const ShiftMarketplaceApprovals: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canApprove = hasPermission('scheduling');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ShiftMarketplaceRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await shiftMarketplaceApi.getRequests({ status: 'PendingApproval' });
      setRows(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load approvals');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (id: number, action: () => Promise<unknown>) => {
    try {
      setBusyId(id);
      setError(null);
      await action();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  if (!canApprove) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
          You need the Scheduling permission to approve marketplace requests.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800">Marketplace Approvals</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Admin review of accepted giveaways and swaps. Approving reassigns the shift roster.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/scheduling"
            className="px-3 py-2 rounded-lg border border-neutral-300 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            Scheduling
          </Link>
          <button
            type="button"
            onClick={load}
            className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-5 w-5 text-blue-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      {loading ? (
        <div className="text-neutral-500 py-12 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-neutral-500 py-12 text-center">No pending marketplace approvals.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div
              key={r.id}
              className="bg-white rounded-lg border border-neutral-200 shadow-sm p-4 space-y-3"
            >
              <div className="flex flex-wrap justify-between gap-2">
                <div>
                  <div className="font-semibold text-neutral-900">
                    {r.requestType === 'Swap' ? 'Swap' : 'Giveaway'} · {r.locationName || `Location #${r.locationId}`}
                  </div>
                  <div className="text-sm text-neutral-600">
                    {r.offeredByName} → {r.acceptedByName || 'acceptor'}
                  </div>
                </div>
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-900">
                  PendingApproval
                </span>
              </div>

              <div className="text-sm text-neutral-700 space-y-1">
                <div>
                  <span className="text-neutral-500">Offered shift: </span>
                  {formatShiftRange(r.offeredShift?.startTime, r.offeredShift?.endTime)}
                  {r.offeredShift?.positionTitle ? ` · ${r.offeredShift.positionTitle}` : ''}
                </div>
                {r.requestedShift && (
                  <div>
                    <span className="text-neutral-500">Requested shift: </span>
                    {formatShiftRange(r.requestedShift.startTime, r.requestedShift.endTime)}
                    {r.requestedShift.employeeName ? ` · ${r.requestedShift.employeeName}` : ''}
                  </div>
                )}
                {r.notes && <div className="italic text-neutral-500">{r.notes}</div>}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => {
                    if (window.confirm('Approve and update the roster?')) {
                      runAction(r.id, () => shiftMarketplaceApi.approve(r.id));
                    }
                  }}
                  className="px-3 py-1.5 rounded-md bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  Approve
                </button>
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => {
                    const reason = window.prompt('Rejection reason (optional):') ?? undefined;
                    runAction(r.id, () => shiftMarketplaceApi.reject(r.id, reason || undefined));
                  }}
                  className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShiftMarketplaceApprovals;
